import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Volume2, Square } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * SpeakButton — Lecture audio (Text-To-Speech) d'une question + choix.
 * Utilise l'API native Web Speech (window.speechSynthesis) — aucune dépendance externe.
 *
 * Props:
 *   - text : string (obligatoire) — texte principal (la question)
 *   - choices : string[] (optionnel) — libellés des choix à lire ensuite (A, B, C, D)
 *   - lang : string — défaut "fr-FR"
 *   - rate : number — vitesse (défaut 0.95 pour une meilleure compréhension)
 *   - pitch : number — tonalité (défaut 1)
 *   - compact : bool — version icône seule (pour les listes denses)
 *   - label : string — texte du bouton (défaut "Écouter")
 *   - testId : string — data-testid
 *
 * Notes:
 *   - Annule automatiquement toute lecture en cours au clic, au changement de props
 *     ou au démontage du composant (évite que la voix continue après navigation).
 *   - Sélectionne automatiquement une voix française si disponible.
 */
const cleanChoiceLabel = (c = '') => String(c).replace(/^[A-D][.:)]\s*/i, '').trim();

const pickFrenchVoice = () => {
  try {
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;
    // Préférences : fr-FR > fr-* > par défaut
    return (
      voices.find((v) => v.lang === 'fr-FR') ||
      voices.find((v) => /^fr[-_]/i.test(v.lang)) ||
      voices.find((v) => /french|français/i.test(v.name)) ||
      null
    );
  } catch {
    return null;
  }
};

export const SpeakButton = ({
  text,
  choices = [],
  lang = 'fr-FR',
  rate = 0.95,
  pitch = 1,
  compact = false,
  label = 'Écouter',
  testId = 'speak-button',
  className = '',
}) => {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(true);
  const utterancesRef = useRef([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      setSupported(false);
    } else {
      // Force le chargement des voix (certains navigateurs sont asynchrones)
      try {
        window.speechSynthesis.getVoices();
        window.speechSynthesis.onvoiceschanged = () => {
          window.speechSynthesis.getVoices();
        };
      } catch {
        // ignore
      }
    }
    return () => {
      mountedRef.current = false;
      try {
        window.speechSynthesis && window.speechSynthesis.cancel();
      } catch {
        // ignore
      }
    };
  }, []);

  // Annule la lecture dès que le texte change (navigation question suivante)
  useEffect(() => {
    try {
      if (window.speechSynthesis && window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }
    } catch {
      // ignore
    }
    setSpeaking(false);
    utterancesRef.current = [];
  }, [text, choices]);

  const stop = useCallback(() => {
    try {
      window.speechSynthesis.cancel();
    } catch {
      // ignore
    }
    setSpeaking(false);
    utterancesRef.current = [];
  }, []);

  const speak = useCallback(() => {
    if (!supported || !text) return;
    try {
      window.speechSynthesis.cancel(); // s'assurer qu'aucune lecture résiduelle
    } catch {
      // ignore
    }

    const voice = pickFrenchVoice();
    const segments = [];
    segments.push(String(text).trim());
    (choices || []).forEach((c, i) => {
      const letter = String.fromCharCode(65 + i);
      segments.push(`${letter}. ${cleanChoiceLabel(c)}`);
    });

    const queue = segments.filter(Boolean).map((seg, i, arr) => {
      const u = new window.SpeechSynthesisUtterance(seg);
      u.lang = lang;
      u.rate = rate;
      u.pitch = pitch;
      if (voice) u.voice = voice;
      u.onend = () => {
        if (!mountedRef.current) return;
        if (i === arr.length - 1) setSpeaking(false);
      };
      u.onerror = () => {
        if (!mountedRef.current) return;
        setSpeaking(false);
      };
      return u;
    });

    utterancesRef.current = queue;
    setSpeaking(true);
    queue.forEach((u) => {
      try {
        window.speechSynthesis.speak(u);
      } catch {
        setSpeaking(false);
      }
    });
  }, [supported, text, choices, lang, rate, pitch]);

  if (!supported) return null;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (speaking) stop();
    else speak();
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid={testId}
        aria-label={speaking ? 'Arrêter la lecture' : 'Écouter la question'}
        title={speaking ? 'Arrêter' : 'Écouter la question'}
        className={cn(
          'inline-flex items-center justify-center rounded-full w-9 h-9 border-2 transition-colors',
          'border-border bg-card hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]',
          speaking && 'border-[hsl(var(--primary))] text-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)] animate-pulse',
          className
        )}
      >
        {speaking ? <Square className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testId}
      aria-label={speaking ? 'Arrêter la lecture' : 'Écouter la question'}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border-2 px-3 py-1.5 text-xs font-bold transition-colors',
        'border-border bg-card hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]',
        speaking && 'border-[hsl(var(--primary))] text-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)] animate-pulse',
        className
      )}
    >
      {speaking ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
      <span>{speaking ? 'Arrêter' : label}</span>
    </button>
  );
};

export default SpeakButton;
